export interface ExportColorEntry {
  name: string;   // e.g. "primary", "coral", "sky-blue"
  hex: string;    // e.g. "#FF6B6B"
  tints: string[];  // 9 values: [0]=lightest(100) … [8]=darkest(900)
  shades: string[]; // 9 values: [0]=lightest(100) … [8]=darkest(900)
  hsl: { h: number; s: number; l: number };
  cmyk: { c: number; m: number; y: number; k: number };
  oklch?: { l: number; c: number; h: number };  // l: 0-100, c: 0-40 (raw×100), h: 0-360
}

export type ExportFormat = 'css' | 'tailwind-v3' | 'tailwind-v4' | 'scss' | 'json' | 'shadcn' | 'daisyui' | 'bootstrap' | 'material-ui' | 'chakra' | 'dtcg' | 'style-dictionary' | 'bookmarklet';

export interface ExportContext {
  /** Public tool path and input query, supplied by the caller; never read browser state here. */
  sourceUrl?: string;
}

export const EXPORT_CALCULATION_VERSION = 'wcag-2.2-srgb-2026-09-14';

/** Keep reproducible color inputs and discard tracking, fragments and unrelated query data. */
export const getExportSourceUrl = (sourceUrl?: string): string | undefined => {
  if (!sourceUrl) return undefined;
  try {
    const source = new URL(sourceUrl, 'https://color-match.io');
    if (source.origin !== 'https://color-match.io') return undefined;
    if (!/^\/(?:studio(?:\/[0-9a-f-]+)?|color\/[0-9a-f]{6}|compare(?:\/[a-z0-9-]+)?|contrast-checker|converter|link-contrast-checker|palette\/[0-9a-f-]+)$/i.test(source.pathname)) return undefined;
    const query = new URLSearchParams();
    const keys = source.pathname === '/contrast-checker' ? ['fg', 'bg']
      : source.pathname === '/converter' ? ['hex']
      : source.pathname === '/link-contrast-checker' ? ['link', 'text', 'bg'] : [];
    for (const key of keys) {
      const value = source.searchParams.get(key)?.replace(/^#/, '');
      if (value && /^[0-9a-f]{6}$/i.test(value)) query.set(key, value.toUpperCase());
    }
    const target = source.searchParams.get('target');
    if (source.pathname === '/contrast-checker' && target && ['3', '4.5', '7'].includes(target)) query.set('target', target);
    const underline = source.searchParams.get('underline');
    if (source.pathname === '/link-contrast-checker' && underline && ['always', 'interaction', 'none'].includes(underline)) query.set('underline', underline);
    return 'https://color-match.io' + source.pathname + (query.size ? '?' + query : '');
  } catch { return undefined; }
};

const STEPS = [100, 200, 300, 400, 500, 600, 700, 800, 900];

const description = (contrastRatio?: number, context?: ExportContext): string => {
  const source = getExportSourceUrl(context?.sourceUrl);
  const ratio = Number.isFinite(contrastRatio) && contrastRatio! >= 1 && contrastRatio! <= 21
    ? ` | Input pair contrast only: ${contrastRatio!.toFixed(3)}:1` : '';
  return `Generated by Color Match — ${source || 'https://color-match.io'}${ratio}`;
};
const header = (contrastRatio?: number, context?: ExportContext): string => `/* ${description(contrastRatio, context)} */\n`;
const metadata = (contrastRatio?: number, context?: ExportContext) => ({
  generator: 'Color Match',
  ...(getExportSourceUrl(context?.sourceUrl) ? { sourceUrl: getExportSourceUrl(context?.sourceUrl) } : {}),
  ...(Number.isFinite(contrastRatio) && contrastRatio! >= 1 && contrastRatio! <= 21
    ? { inputPairContrast: { ratio: contrastRatio, scope: 'Input pair only; generated roles require separate checks.', calculationVersion: EXPORT_CALCULATION_VERSION } } : {}),
});

export const formatCSS = (colors: ExportColorEntry[], contrastRatio?: number, context?: ExportContext): string => {
  const lines: string[] = [header(contrastRatio, context), ':root {'];
  for (const color of colors) {
    const hsl = `hsl(${color.hsl.h}, ${color.hsl.s}%, ${color.hsl.l}%)`;
    const cmyk = `cmyk(${color.cmyk.c}%, ${color.cmyk.m}%, ${color.cmyk.y}%, ${color.cmyk.k}%)`;
    const oklchStr = color.oklch
      ? ` — oklch(${color.oklch.l}% ${(color.oklch.c / 100).toFixed(3)} ${color.oklch.h})`
      : '';
    lines.push(`  /* ${color.name} — ${hsl} — ${cmyk}${oklchStr} */`);
    lines.push(`  --color-${color.name}: ${color.hex};`);
    if (color.oklch) {
      lines.push(`  --color-${color.name}-oklch: oklch(${color.oklch.l}% ${(color.oklch.c / 100).toFixed(3)} ${color.oklch.h});`);
    }
    color.tints.forEach((hex, i) => {
      lines.push(`  --color-${color.name}-tint-${STEPS[i]}: ${hex};`);
    });
    color.shades.forEach((hex, i) => {
      lines.push(`  --color-${color.name}-shade-${STEPS[i]}: ${hex};`);
    });
  }
  lines.push('}');
  return lines.join('\n');
};

export const formatTailwindV3 = (colors: ExportColorEntry[], contrastRatio?: number, context?: ExportContext): string => {
  const lines: string[] = [
    `// tailwind.config.js — ${description(contrastRatio, context)}`,
    `module.exports = {`,
    `  theme: {`,
    `    extend: {`,
    `      colors: {`,
  ];
  for (const color of colors) {
    lines.push(`        ${JSON.stringify(color.name)}: {`);
    lines.push(`          DEFAULT: '${color.hex}',`);
    color.tints.forEach((hex, i) => {
      lines.push(`          'tint-${STEPS[i]}': '${hex}',`);
    });
    color.shades.forEach((hex, i) => {
      lines.push(`          'shade-${STEPS[i]}': '${hex}',`);
    });
    lines.push(`        },`);
  }
  lines.push(`      },`);
  lines.push(`    },`);
  lines.push(`  },`);
  lines.push(`};`);
  return lines.join('\n');
};

export const formatTailwindV4 = (colors: ExportColorEntry[], contrastRatio?: number, context?: ExportContext): string => {
  const lines: string[] = [header(contrastRatio, context), '@theme {'];
  for (const color of colors) {
    lines.push(`  --color-${color.name}: ${color.hex};`);
    color.tints.forEach((hex, i) => {
      lines.push(`  --color-${color.name}-tint-${STEPS[i]}: ${hex};`);
    });
    color.shades.forEach((hex, i) => {
      lines.push(`  --color-${color.name}-shade-${STEPS[i]}: ${hex};`);
    });
  }
  lines.push('}');
  return lines.join('\n');
};

export const formatSCSS = (colors: ExportColorEntry[], contrastRatio?: number, context?: ExportContext): string => {
  const lines: string[] = [`// ${description(contrastRatio, context)}`];
  for (const color of colors) {
    const hsl = `hsl(${color.hsl.h}, ${color.hsl.s}%, ${color.hsl.l}%)`;
    lines.push('');
    lines.push(`$color-${color.name}: ${color.hex}; // ${hsl}`);
    color.tints.forEach((hex, i) => {
      lines.push(`$color-${color.name}-tint-${STEPS[i]}: ${hex};`);
    });
    color.shades.forEach((hex, i) => {
      lines.push(`$color-${color.name}-shade-${STEPS[i]}: ${hex};`);
    });
  }
  return lines.join('\n');
};

export const formatJSON = (colors: ExportColorEntry[], contrastRatio?: number, context?: ExportContext): string => {
  const result: Record<string, unknown> = { $metadata: metadata(contrastRatio, context) };
  const colorTokens: Record<string, unknown> = {};
  for (const color of colors) {
    const entry: Record<string, unknown> = {
      DEFAULT: { value: color.hex, type: 'color' },
    };
    color.tints.forEach((hex, i) => {
      entry[`tint-${STEPS[i]}`] = { value: hex, type: 'color' };
    });
    color.shades.forEach((hex, i) => {
      entry[`shade-${STEPS[i]}`] = { value: hex, type: 'color' };
    });
    colorTokens[color.name] = entry;
  }
  result['color'] = colorTokens;
  return JSON.stringify(result, null, 2);
};

// --- Framework-specific presets ---

const hslFromHex = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const rf = r / 255, gf = g / 255, bf = b / 255;
  const max = Math.max(rf, gf, bf), min = Math.min(rf, gf, bf);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rf: h = ((gf - bf) / d + (gf < bf ? 6 : 0)); break;
      case gf: h = ((bf - rf) / d + 2); break;
      case bf: h = ((rf - gf) / d + 4); break;
    }
    h *= 60;
  }
  return `${Number(h.toFixed(5))} ${Number((s * 100).toFixed(5))}% ${Number((l * 100).toFixed(5))}%`;
};

/** Evaluate the emitted CSS HSL components without rounding them back to 8-bit HEX. */
const tokenLuminance = (token: string): number => {
  const [h, sPercent, lPercent] = token.split(/\s+/).map(parseFloat);
  const s = sPercent / 100, l = lPercent / 100;
  const a = s * Math.min(l, 1 - l);
  const channel = (n: number) => {
    const k = (n + h / 30) % 12;
    const value = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(8) + 0.0722 * channel(4);
};
const tokenContrast = (foreground: string, background: string): number => {
  const a = tokenLuminance(foreground), b = tokenLuminance(background);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
};
const foregroundFor = (background: string): string =>
  tokenContrast('0 0% 0%', background) >= tokenContrast('0 0% 100%', background) ? '0 0% 0%' : '0 0% 100%';

export interface ExportRoleCheck {
  foregroundToken: string;
  backgroundToken: string;
  foreground: string;
  background: string;
  ratio: number;
  passes: boolean;
}

const frameworkTokens = (format: 'shadcn' | 'daisyui', colors: ExportColorEntry[]) => {
  if (!colors.length) return { tokens: {} as Record<string, string>, pairs: [] as string[][] };
  const primary = colors[0];
  const secondary = colors[1] || colors[0];
  const pairs = format === 'shadcn'
    ? [['foreground', 'background'], ['primary-foreground', 'primary'], ['secondary-foreground', 'secondary'], ['accent-foreground', 'accent'], ['muted-foreground', 'muted'], ['destructive-foreground', 'destructive']]
    : [['pc', 'p'], ['pc', 'pf'], ['sc', 's'], ['sc', 'sf'], ['ac', 'a'], ['ac', 'af'], ['nc', 'n'], ['nc', 'nf'], ['bc', 'b1'], ['bc', 'b2'], ['bc', 'b3']];
  const tokens: Record<string, string> = format === 'shadcn' ? {
    background: '0 0% 100%', primary: hslFromHex(primary.hex), secondary: hslFromHex(secondary.hex),
    accent: hslFromHex(primary.tints[3] || primary.hex), muted: hslFromHex(primary.tints[0] || '#F4F4F5'),
    destructive: '0 84.2% 60.2%', border: '0 0% 89.8%', ring: hslFromHex(primary.hex), radius: '0.5rem',
  } : {
    p: hslFromHex(primary.hex), pf: hslFromHex(primary.shades[2] || primary.hex),
    s: hslFromHex(secondary.hex), sf: hslFromHex(secondary.shades[2] || secondary.hex),
    a: hslFromHex(primary.tints[3] || primary.hex), af: hslFromHex(primary.tints[5] || primary.hex),
    n: '220 13% 18%', nf: '220 13% 13%', b1: '0 0% 100%', b2: '0 0% 96%', b3: '0 0% 92%',
  };
  // Shared DaisyUI content tokens are chosen for the base state; every focus state is checked separately.
  for (const [foreground, background] of pairs) if (!tokens[foreground]) tokens[foreground] = foregroundFor(tokens[background]);
  return { tokens, pairs };
};

export const getExportRoleChecks = (format: ExportFormat, colors: ExportColorEntry[]): ExportRoleCheck[] => {
  if (format !== 'shadcn' && format !== 'daisyui') return [];
  const { tokens, pairs } = frameworkTokens(format, colors);
  return pairs.map(([foregroundToken, backgroundToken]) => {
    const ratio = tokenContrast(tokens[foregroundToken], tokens[backgroundToken]);
    return { foregroundToken, backgroundToken, foreground: `hsl(${tokens[foregroundToken]})`, background: `hsl(${tokens[backgroundToken]})`, ratio, passes: ratio >= 4.5 };
  });
};

const formatFramework = (format: 'shadcn' | 'daisyui', colors: ExportColorEntry[], contrastRatio?: number, context?: ExportContext): string => {
  const { tokens } = frameworkTokens(format, colors);
  const checks = getExportRoleChecks(format, colors);
  const lines = [header(contrastRatio, context), `/* ${format === 'shadcn' ? 'shadcn/ui legacy HSL variables (Tailwind v3)' : 'DaisyUI v3 HSL theme'} */`,
    `/* Text checks: ${EXPORT_CALCULATION_VERSION}; opaque emitted sRGB HSL; normal text AA >= 4.5:1.`,
    ...checks.map(check => ` * --${check.foregroundToken} on --${check.backgroundToken}: ${check.ratio.toFixed(3)}:1 ${check.passes ? 'PASS' : 'FAIL'}`),
    ' * Check boundaries, focus indicators, opacity and component overrides in your application.',
    ' */', format === 'shadcn' ? '@layer base {\n  :root {' : '[data-theme="custom"] {'];
  for (const [name, value] of Object.entries(tokens)) lines.push(`    --${name}: ${value};`);
  lines.push(format === 'shadcn' ? '  }\n}' : '}');
  return lines.join('\n');
};
export const formatShadcn = (colors: ExportColorEntry[], contrastRatio?: number, context?: ExportContext): string => formatFramework('shadcn', colors, contrastRatio, context);
export const formatDaisyUI = (colors: ExportColorEntry[], contrastRatio?: number, context?: ExportContext): string => formatFramework('daisyui', colors, contrastRatio, context);

export const formatBootstrap = (colors: ExportColorEntry[], contrastRatio?: number, context?: ExportContext): string => {
  const lines: string[] = [`// Bootstrap SCSS Variables — ${description(contrastRatio, context)}\n`];
  const primary = colors[0];
  const secondary = colors[1] || colors[0];

  lines.push(`$primary: ${primary.hex};`);
  lines.push(`$secondary: ${secondary.hex};`);
  lines.push(`$success: #198754;`);
  lines.push(`$info: #0dcaf0;`);
  lines.push(`$warning: #ffc107;`);
  lines.push(`$danger: #dc3545;`);
  lines.push(`$light: ${primary.tints[0] || '#f8f9fa'};`);
  lines.push(`$dark: ${primary.shades[7] || '#212529'};`);
  lines.push('');
  lines.push('// Tint/Shade scales');
  for (const color of colors) {
    lines.push(`\n// ${color.name}`);
    lines.push(`$${color.name}: ${color.hex};`);
    color.tints.forEach((hex, i) => {
      lines.push(`$${color.name}-tint-${STEPS[i]}: ${hex};`);
    });
    color.shades.forEach((hex, i) => {
      lines.push(`$${color.name}-shade-${STEPS[i]}: ${hex};`);
    });
  }
  return lines.join('\n');
};

export const formatMaterialUI = (colors: ExportColorEntry[], contrastRatio?: number, context?: ExportContext): string => {
  const lines: string[] = [
    `// Material UI Theme — ${description(contrastRatio, context)}`,
    `import { createTheme } from '@mui/material/styles';\n`,
    `const theme = createTheme({`,
    `  palette: {`,
  ];
  const primary = colors[0];
  const secondary = colors[1] || colors[0];

  lines.push(`    primary: {`);
  lines.push(`      main: '${primary.hex}',`);
  lines.push(`      light: '${primary.tints[3] || primary.hex}',`);
  lines.push(`      dark: '${primary.shades[3] || primary.hex}',`);
  lines.push(`    },`);
  lines.push(`    secondary: {`);
  lines.push(`      main: '${secondary.hex}',`);
  lines.push(`      light: '${secondary.tints[3] || secondary.hex}',`);
  lines.push(`      dark: '${secondary.shades[3] || secondary.hex}',`);
  lines.push(`    },`);
  lines.push(`  },`);
  lines.push(`});\n`);
  lines.push(`export default theme;`);
  return lines.join('\n');
};

export const formatChakra = (colors: ExportColorEntry[], contrastRatio?: number, context?: ExportContext): string => {
  const lines: string[] = [
    `// Chakra UI Theme — ${description(contrastRatio, context)}`,
    `import { extendTheme } from '@chakra-ui/react';\n`,
    `const theme = extendTheme({`,
    `  colors: {`,
  ];

  for (const color of colors) {
    lines.push(`    ${color.name}: {`);
    // Map tints/shades to 50-900 scale
    const allSteps = [color.tints[0], color.tints[2], color.tints[4], color.tints[6], color.tints[8], color.hex, color.shades[1], color.shades[3], color.shades[5], color.shades[7]];
    const stepLabels = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
    stepLabels.forEach((step, i) => {
      lines.push(`      ${step}: '${allSteps[i] || color.hex}',`);
    });
    lines.push(`    },`);
  }

  lines.push(`  },`);
  lines.push(`});\n`);
  lines.push(`export default theme;`);
  return lines.join('\n');
};

// ── Design Token Formats ───────────────────────────

/** Design Tokens Community Group 2025.10 color values. */
const dtcgColor = (hex: string) => ({
  colorSpace: 'srgb',
  components: [1, 3, 5].map(offset => parseInt(hex.slice(offset, offset + 2), 16) / 255),
  alpha: 1,
  hex: hex.toLowerCase(),
});
export const formatDTCG = (colors: ExportColorEntry[], contrastRatio?: number, context?: ExportContext): string => {
  const tokens: Record<string, unknown> = {
    $description: description(contrastRatio, context),
    $extensions: { 'io.color-match': metadata(contrastRatio, context) },
  };

  const colorGroup: Record<string, unknown> = {
    $type: 'color',
    $description: 'Color palette tokens',
  };

  for (const color of colors) {
    const group: Record<string, unknown> = {
      base: { $value: dtcgColor(color.hex), $description: `${color.name} base color` },
    };
    STEPS.forEach((step, i) => {
      group[`tint-${step}`] = { $value: dtcgColor(color.tints[i]) };
      group[`shade-${step}`] = { $value: dtcgColor(color.shades[i]) };
    });
    colorGroup[color.name] = group;
  }

  tokens.color = colorGroup;
  return JSON.stringify(tokens, null, 2);
};

/** Style Dictionary CTI (Category/Type/Item) format */
export const formatStyleDictionary = (colors: ExportColorEntry[], contrastRatio?: number, context?: ExportContext): string => {
  const result: Record<string, unknown> = { _metadata: metadata(contrastRatio, context) };
  const colorTokens: Record<string, unknown> = {};
  for (const color of colors) {
    const group: Record<string, unknown> = {
      base: { value: color.hex.toLowerCase() },
    };
    STEPS.forEach((step, i) => {
      group[`tint-${step}`] = { value: color.tints[i].toLowerCase() };
      group[`shade-${step}`] = { value: color.shades[i].toLowerCase() };
    });
    colorTokens[color.name] = group;
  }
  result.color = colorTokens;
  return JSON.stringify(result, null, 2);
};

/** CSS variable injection bookmarklet */
export const formatBookmarklet = (colors: ExportColorEntry[], _contrastRatio?: number, context?: ExportContext): string => {
  const vars = colors
    .map(c => `--color-${c.name}:${c.hex}`)
    .join(';');
  const source = getExportSourceUrl(context?.sourceUrl);
  return `javascript:${source ? `/* Source: ${source} */` : ''}void(document.documentElement.style.cssText+=${JSON.stringify(';' + vars)})`;
};

export const FORMATTERS: Record<ExportFormat, (colors: ExportColorEntry[], contrastRatio?: number, context?: ExportContext) => string> = {
  'css': formatCSS,
  'tailwind-v3': formatTailwindV3,
  'tailwind-v4': formatTailwindV4,
  'scss': formatSCSS,
  'json': formatJSON,
  'shadcn': formatShadcn,
  'daisyui': formatDaisyUI,
  'bootstrap': formatBootstrap,
  'material-ui': formatMaterialUI,
  'chakra': formatChakra,
  'dtcg': formatDTCG,
  'style-dictionary': formatStyleDictionary,
  'bookmarklet': formatBookmarklet,
};
